import i18n from 'i18next';
import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
} from 'mobx';

import { ActionKind, ActionParams } from 'components/ui';
import { FieldDefaultValueEntityApi } from 'entities/FieldDefaultValue';
import { FieldDefaultValueKind } from 'entities/common/fieldDefaultValue';
import { Unsubscribe } from 'stores/common';
import { ILocalStore, IServerEntity } from 'stores/interfaces';
import { TableRowEntityType } from 'stores/local/forms/types';
import { NewFormulaModel } from 'stores/models/NewFormulaModel';
import { noop } from 'utils/noop';

import { FieldSettingsStoreCommonParams } from '../../types';
import { IDictionarySettingsStore } from '../interface';

type PrivateFields =
  | '_defaultValueDisplayMode'
  | '_defaultValue'
  | '_editingFormula'
  | '_currentEntity'
  | '_setDefaultValueDisplayMode';

type DictionarySettingsDefaultValueParams = {
  store: IDictionarySettingsStore;
  setHighlightedRows: (keys: string[]) => void;
} & Omit<FieldSettingsStoreCommonParams, 'mediator'>;

const DEFAULT_VALUE_ID_NULLISH = '';

/** Стор для работы со значением по умолчанию в справочнике */
class DictionarySettingsDefaultValue
  implements ILocalStore, IServerEntity<FieldDefaultValueEntityApi>
{
  constructor({
    store,
    getTableRowEntityByCatalogId,
    setHighlightedRows,
  }: DictionarySettingsDefaultValueParams) {
    this._setHighlightedRows = setHighlightedRows;

    this.newFormulaModel = new NewFormulaModel({
      getLabelByFormFieldEntityId: (id) =>
        getTableRowEntityByCatalogId(id)?.catalogField.label || '',
    });

    this._parentStore = store;

    makeObservable<DictionarySettingsDefaultValue, PrivateFields>(this, {
      _defaultValueDisplayMode: observable,
      defaultValueDisplayMode: computed,
      _setDefaultValueDisplayMode: action.bound,

      init: action.bound,

      selectedValueLabel: computed,

      _defaultValue: observable.ref,
      defaultValue: computed,
      setDefaultValue: action.bound,
      resetDefaultValueId: action.bound,
      reset: action.bound,

      _editingFormula: observable,
      editingFormula: computed,
      setEditingFormulaState: action.bound,
      handleSelectRow: action.bound,

      _currentEntity: observable.ref,
      setCurrentEntity: action.bound,

      setDataDisplayMode: action.bound,
      setFormulaDisplayMode: action.bound,

      resetDefaultIdAction: computed,

      clearIconParams: computed,
      editButtonParams: computed,
    });
  }

  private _parentStore: IDictionarySettingsStore;

  private readonly _setHighlightedRows: (ids: string[]) => void;

  private _reaction: IReactionDisposer | null = null;

  private _rowSelectSubscriber: Unsubscribe | null = null;

  readonly newFormulaModel: NewFormulaModel;

  /** Текущий режим отображения для выбора значения по умолчанию ( формула или значение справочника )*/
  private _defaultValueDisplayMode: FieldDefaultValueKind =
    FieldDefaultValueKind.data;

  /** Редактируем формулу или нет*/
  private _editingFormula = false;

  /** Текущая выбранная сущность из таблицы */
  private _currentEntity: TableRowEntityType | null = null;

  /* ID значения по умолчанию*/
  private _defaultValue: string = DEFAULT_VALUE_ID_NULLISH;

  get defaultValue(): string {
    return this._defaultValue;
  }

  /** Пропсы для рендера иконки для очистки значения по умолчанию */
  get clearIconParams(): { display: boolean; action: ActionParams } {
    if (
      this._defaultValueDisplayMode === FieldDefaultValueKind.data &&
      this.selectedValueLabel
    ) {
      return {
        display: true,
        action: {
          actionKind: ActionKind.button,
          complexOnClick: {
            onClick: this.resetDefaultValueId,
          },
        },
      };
    }

    if (
      this.defaultValueDisplayMode === FieldDefaultValueKind.formula &&
      !this.editingFormula
    ) {
      return {
        display: true,
        action: {
          actionKind: ActionKind.button,
          complexOnClick: {
            onClick: this.newFormulaModel.reset,
          },
        },
      };
    }

    return {
      display: false,
      action: {
        actionKind: ActionKind.button,
        complexOnClick: {
          onClick: noop,
        },
      },
    };
  }

  get editButtonParams(): {
    display: boolean;
    text: string;
    action: VoidFunction;
  } {
    if (this._defaultValueDisplayMode === FieldDefaultValueKind.data) {
      return {
        display: true,
        text: i18n.t('dictionary.setViaFormula') as string,
        action: this.setFormulaDisplayMode,
      };
    }

    if (!this._editingFormula) {
      return {
        display: true,
        text: i18n.t('common.change') as string,
        action: () => this.setEditingFormulaState(true),
      };
    }

    return {
      display: false,
      text: '',
      action: () => {},
    };
  }

  get defaultValueDisplayMode(): FieldDefaultValueKind {
    return this._defaultValueDisplayMode;
  }

  /** Лейбл выбранного айдишника из значений справочника */
  get selectedValueLabel(): string {
    return (
      this._parentStore.values.find((item) => item.value === this.defaultValue)
        ?.label || ''
    );
  }

  get editingFormula(): boolean {
    return this._editingFormula;
  }

  get toServerFormat(): FieldDefaultValueEntityApi {
    if (this._defaultValueDisplayMode === FieldDefaultValueKind.formula) {
      return {
        kind: FieldDefaultValueKind.formula,
        value: this.newFormulaModel.toServerFormat,
      };
    }

    /** Если так и не выбрали какое-либо значение, то отправляем null */
    if (!this._defaultValue) {
      return null;
    }

    return {
      kind: FieldDefaultValueKind.data,
      value: this._defaultValue,
    };
  }

  get resetDefaultIdAction(): ActionParams {
    return {
      actionKind: ActionKind.button,
      complexOnClick: {
        onClick: this.resetDefaultValueId,
      },
    };
  }

  setCurrentEntity(entity: TableRowEntityType | null): void {
    this._currentEntity = entity;
  }

  resetRowSelectSubscription(): void {
    this._reaction?.();
    this._rowSelectSubscriber?.();
    this._reaction = null;
    this._rowSelectSubscriber = null;
  }

  init(entity: TableRowEntityType): void {
    /* Если нет значения по умолчанию, то считаем, что выбран режим отображения выбора значения из значений самого справочника */
    if (!entity.formField.defaultValue) {
      this._defaultValue = DEFAULT_VALUE_ID_NULLISH;
      this._defaultValueDisplayMode = FieldDefaultValueKind.data;

      return;
    }

    if (entity.formField.defaultValue.kind === FieldDefaultValueKind.data) {
      this._defaultValue = entity.formField.defaultValue.value;
      this._defaultValueDisplayMode = FieldDefaultValueKind.data;

      return;
    }

    this.newFormulaModel.fromServerFormat(entity.formField.defaultValue.value);
    this._defaultValueDisplayMode = FieldDefaultValueKind.formula;
  }

  setEditingFormulaState(newValue = false): void {
    this._editingFormula = newValue;
  }

  setFormulaDisplayMode(): void {
    this._setDefaultValueDisplayMode(FieldDefaultValueKind.formula);
  }

  setDataDisplayMode(): void {
    this._setDefaultValueDisplayMode(FieldDefaultValueKind.data);
  }

  handleSelectRow(entity: TableRowEntityType): void {
    if (
      this._defaultValueDisplayMode === FieldDefaultValueKind.formula &&
      this._editingFormula
    ) {
      this.newFormulaModel.addEntity(entity.formField);
    }
  }

  private _setDefaultValueDisplayMode(mode: FieldDefaultValueKind): void {
    if (mode === FieldDefaultValueKind.formula) {
      this._editingFormula = true;
      this._defaultValue = '';
    }

    this._defaultValueDisplayMode = mode;
  }

  setDefaultValue(value: string): void {
    /** При любом выборе значения справочника переключаем набор значения по умолчанию*/
    this._setDefaultValueDisplayMode(FieldDefaultValueKind.data);

    this._defaultValue = value;
  }

  reset(): void {
    this._defaultValue = DEFAULT_VALUE_ID_NULLISH;
    this._defaultValueDisplayMode = FieldDefaultValueKind.data;
    this.newFormulaModel.reset();
    this._editingFormula = false;
  }

  resetDefaultValueId(): void {
    this._defaultValue = DEFAULT_VALUE_ID_NULLISH;
  }

  destroy = (): void => {
    this.resetRowSelectSubscription();
    this._setHighlightedRows([]);
  };
}

export default DictionarySettingsDefaultValue;
